import { apiService } from './api';
import { Workflow, Node, Connection, Execution, WorkflowSchedule, WorkflowVersion, WorkflowCredential } from '../types/workflow.types';
import { FilterParams, PaginatedResponse } from '../types/api.types';

export const workflowService = {
  // Workflow management
  getWorkflows: async (params?: FilterParams): Promise<PaginatedResponse<Workflow>> => {
    return await apiService.get<PaginatedResponse<Workflow>>('/workflows/', params);
  },

  getWorkflow: async (id: string): Promise<Workflow> => {
    return await apiService.get<Workflow>(`/workflows/${id}/`);
  },

  createWorkflow: async (data: Partial<Workflow>): Promise<Workflow> => {
    return await apiService.post<Workflow>('/workflows/', data);
  },

  updateWorkflow: async (id: string, data: Partial<Workflow>): Promise<Workflow> => {
    return await apiService.patch<Workflow>(`/workflows/${id}/`, data);
  },

  deleteWorkflow: async (id: string): Promise<void> => {
    await apiService.delete(`/workflows/${id}/`);
  },

  // Workflow execution
  executeWorkflow: async (
    id: string,
    trigger_data?: any
  ): Promise<{ execution_id: string; status: string }> => {
    return await apiService.post(`/workflows/${id}/execute/`, trigger_data ? { trigger_data } : {});
  },

  executeChatWorkflow: async (
    id: string,
    chatInput: string,
    sessionId?: string
  ): Promise<{ execution_id: string; status: string; sessionId?: string }> => {
    return await apiService.post(`/workflows/${id}/chat/`, {
      chatInput,
      sessionId,
    });
  },

  // Workflow import/export
  exportWorkflow: async (id: string): Promise<any> => {
    return await apiService.get(`/workflows/${id}/export/`);
  },

  importWorkflow: async (data: any): Promise<Workflow> => {
    // Imports can be slow on large n8n workflows (50+ nodes, many connections)
    // because the backend has to write everything inside one transaction. The
    // default 30s axios timeout was too tight on transcontinental connections;
    // 2 minutes gives plenty of headroom even with the bulk-insert speedup.
    return await apiService.post('/workflows/import_workflow/', data, { timeout: 120000 });
  },

  duplicateWorkflow: async (id: string): Promise<Workflow> => {
    return await apiService.post(`/workflows/${id}/duplicate/`);
  },

  validateWorkflow: async (id: string): Promise<{ valid: boolean; message: string }> => {
    return await apiService.post(`/workflows/${id}/validate_workflow/`);
  },

  // Node management
  getNodes: async (workflowId: string): Promise<Node[]> => {
    return await apiService.get<Node[]>(`/workflows/${workflowId}/nodes/`);
  },

  createNode: async (workflowId: string, data: Partial<Node>): Promise<Node> => {
    return await apiService.post<Node>(`/workflows/${workflowId}/nodes/`, data);
  },

  updateNode: async (workflowId: string, nodeId: string, data: Partial<Node>): Promise<Node> => {
    return await apiService.patch<Node>(`/workflows/${workflowId}/nodes/${nodeId}/`, data);
  },

  deleteNode: async (workflowId: string, nodeId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/nodes/${nodeId}/`);
  },

  // Connection management
  getConnections: async (workflowId: string): Promise<Connection[]> => {
    return await apiService.get<Connection[]>(`/workflows/${workflowId}/connections/`);
  },

  createConnection: async (workflowId: string, data: Partial<Connection>): Promise<Connection> => {
    return await apiService.post<Connection>(`/workflows/${workflowId}/connections/`, data);
  },

  updateConnection: async (workflowId: string, connectionId: string, data: Partial<Connection>): Promise<Connection> => {
    return await apiService.patch<Connection>(`/workflows/${workflowId}/connections/${connectionId}/`, data);
  },

  deleteConnection: async (workflowId: string, connectionId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/connections/${connectionId}/`);
  },

  // Execution management
  getExecutions: async (params?: FilterParams): Promise<PaginatedResponse<Execution>> => {
    return await apiService.get<PaginatedResponse<Execution>>('/executions/', params);
  },

  getExecution: async (id: string): Promise<Execution> => {
    return await apiService.get<Execution>(`/executions/${id}/`);
  },

  getWorkflowExecutions: async (workflowId: string, params?: FilterParams): Promise<PaginatedResponse<Execution>> => {
    return await apiService.get<PaginatedResponse<Execution>>(`/workflows/${workflowId}/executions/`, params);
  },

  // Schedule management
  getSchedules: async (workflowId: string): Promise<WorkflowSchedule[]> => {
    return await apiService.get<WorkflowSchedule[]>(`/workflows/${workflowId}/schedules/`);
  },

  createSchedule: async (workflowId: string, data: Partial<WorkflowSchedule>): Promise<WorkflowSchedule> => {
    return await apiService.post<WorkflowSchedule>(`/workflows/${workflowId}/schedules/`, data);
  },

  updateSchedule: async (workflowId: string, scheduleId: string, data: Partial<WorkflowSchedule>): Promise<WorkflowSchedule> => {
    return await apiService.patch<WorkflowSchedule>(`/workflows/${workflowId}/schedules/${scheduleId}/`, data);
  },

  deleteSchedule: async (workflowId: string, scheduleId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/schedules/${scheduleId}/`);
  },

  // Workflow credentials (reusable per workflow)
  getCredentials: async (workflowId: string): Promise<WorkflowCredential[]> => {
    return await apiService.get<WorkflowCredential[]>(`/workflows/${workflowId}/credentials/`);
  },

  createCredential: async (
    workflowId: string,
    data: Partial<WorkflowCredential>
  ): Promise<WorkflowCredential> => {
    return await apiService.post<WorkflowCredential>(`/workflows/${workflowId}/credentials/`, data);
  },

  updateCredential: async (
    workflowId: string,
    credentialId: string,
    data: Partial<WorkflowCredential>
  ): Promise<WorkflowCredential> => {
    return await apiService.patch<WorkflowCredential>(`/workflows/${workflowId}/credentials/${credentialId}/`, data);
  },

  deleteCredential: async (workflowId: string, credentialId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/credentials/${credentialId}/`);
  },

  // Resolve a stored OAuth credential into a usable (auto-refreshed) access
  // token. Used by the editor's "Load spreadsheets / Load files / Load
  // calendars" helpers so users never have to copy-paste a token.
  getCredentialAccessToken: async (
    workflowId: string,
    credentialId: string
  ): Promise<{
    access_token: string;
    token_type: string;
    expires_at: string;
    email: string;
    credential_type: string;
  }> => {
    return await apiService.get(
      `/workflows/${workflowId}/credentials/${credentialId}/access-token/`
    );
  },

  // Odoo introspection helpers: let the editor populate Model + Field
  // dropdowns from the user's actual Odoo instance instead of asking
  // them to remember "res.partner" / "x_studio_some_field".
  listOdooModels: async (
    workflowId: string,
    credentialId: string,
    query: string = '',
    limit: number = 0
  ): Promise<{
    models: Array<{ model: string; name: string }>;
    count: number;
    truncated?: boolean;
    warning?: string;
  }> => {
    // Build query string. Default behaviour is "load every model from this
    // Odoo instance" so the editor can show the full universe and the
    // Autocomplete can filter client-side without round-trips.
    const qs = new URLSearchParams();
    if (query) qs.set('q', query);
    if (limit > 0) qs.set('limit', String(limit));
    const tail = qs.toString();
    return await apiService.get(
      `/workflows/${workflowId}/credentials/${credentialId}/odoo-models/${
        tail ? `?${tail}` : ''
      }`
    );
  },

  getOdooModelFields: async (
    workflowId: string,
    credentialId: string,
    model: string
  ): Promise<{
    model: string;
    fields: Array<{
      name: string;
      label: string;
      type: string;
      required: boolean;
      readonly: boolean;
      help: string;
      relation: string;
    }>;
    count: number;
    warning?: string;
  }> => {
    return await apiService.get(
      `/workflows/${workflowId}/credentials/${credentialId}/odoo-fields/?model=${encodeURIComponent(model)}`
    );
  },

  // Version management
  getVersions: async (workflowId: string): Promise<WorkflowVersion[]> => {
    return await apiService.get<WorkflowVersion[]>(`/workflows/${workflowId}/versions/`);
  },

  createVersion: async (workflowId: string, data: { name: string; description?: string }): Promise<WorkflowVersion> => {
    return await apiService.post<WorkflowVersion>(`/workflows/${workflowId}/create_version/`, data);
  },

  // AI-powered workflow editing
  aiEdit: async (
    workflowId: string,
    payload: { prompt: string; nodes: any[]; connections: any[] }
  ): Promise<{
    explanation: string;
    add_nodes: any[];
    add_connections: any[];
    remove_nodes: string[];
    update_nodes: any[];
  }> => {
    return await apiService.post(`/workflows/${workflowId}/ai-edit/`, payload);
  },

  // AI-powered workflow generation from prompt
  aiGenerate: async (
    payload: { prompt: string }
  ): Promise<{ workflow_id: string; explanation: string }> => {
    return await apiService.post('/workflows/ai-generate/', payload);
  },

  // Templates
  getTemplates: async (): Promise<any[]> => {
    return await apiService.get('/workflows/templates/');
  },
};